import { useEffect, useState } from "react";
import { AnimatePresence } from "motion/react";
import { useGetMaxDate, useGetThemeDetails } from "../api";
import ThemeSelection from "./theme-selection/ThemeSelection";
import Reasoning from "./reasoning/Reasoning";
import DateSelection from "./date-selection/DateSelection";
import ThemePostResult from "./theme-result/ThemePostResult";
import { ChatMessageType } from "../types";
import SuccessfulEvent from "./success/SuccessfulEvent";
import ThemeNavigation from "./navigation/ThemeNavigation";
import CreatePostTheme from "./create-post/CreatePostTheme";
import { addMonths, format } from "date-fns";
import SocialCalendar from "./social-calendar/SocialCalendar";
import { cn } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";
import { parseInt } from "lodash";
import ThemeCampaignResult from "./theme-result/ThemeCampaignResult";
import ThemeCampaignSelection from "./campaign-selection/ThemeCampaignSelection";
import StorySelection from "./campaign-selection/StorySelection";
import ThemeTemplateResult from "./theme-result/ThemeTemplateResult";

const ChatWindow = () => {

  const [searchParams, setSearchParams] = useSearchParams()

  const [step, setStep] = useState<ChatMessageType>(searchParams.get("curr") as ChatMessageType || "navigation")

    const [imageId, setImageId] = useState<number | undefined>(searchParams.get("i") !== null
    ? parseInt(searchParams.get("i") as string)
    : undefined)

  const [storyId, setStoryId] = useState<number | undefined>(searchParams.get("s") !== null
    ? parseInt(searchParams.get("s") as string)
    : undefined)

  useEffect(() => {
    if (searchParams.get("curr") !== null) {
      setStep(searchParams.get("curr") as ChatMessageType)
    }
    const image = searchParams.get("i") 
    if(image !== null) {
      setImageId(parseInt(image))
    }
  }, [searchParams])

  const [date, setDate] = useState<Date | undefined>(new Date())

  const [status, setStatus] = useState<number | undefined>(1)

  const [reasoning, setReasoning] = useState<{ name: string, why: string } | undefined>(undefined)

  const [selectedTheme, setSelectedTheme] = useState<number | undefined>(searchParams.get("theme") !== null
    ? parseInt(searchParams.get("theme") as string)
    : undefined)

  const { themeDetailData } = useGetThemeDetails(selectedTheme, storyId)

  const [canReturn, setCanReturn] = useState(true)

  const [message, setMessage] = useState<{ assistant: string, client: string }>({ assistant: "", client: "" })

  const messageHandler = (assistant: string, client: string) => {
    setMessage({ assistant, client })
  }

  const themeSelectHandler = (id: number, nav: string) => {

    setStoryId(undefined)

    setSearchParams({ curr: nav, theme: `${id}` })
    setSelectedTheme(id)
  }

  const storySelectHandler = (id: number, nav: string, story_id: number) => {

    setSearchParams({ curr: nav, theme: `${id}` })
    setStoryId(story_id)
    setSelectedTheme(id)
  }

  const themeSelectFromCreatePostHandler = (id: number) => {
    setSearchParams({ theme: `${id}` })
    setSelectedTheme(id)
  }

  const reasoningHandler = (name: string, why: string) => {
    setReasoning({ name, why })
    // setStep("reasoning")
    setSearchParams({ curr: "reasoning" })
  }

  const stepHandler = (id: ChatMessageType) => {
    // setStep(id)
    setSearchParams({ curr: id })
  }

  const setStatusHandler = (id: number) => {
    setStatus(id)
  }

  const { maxEndDate } = useGetMaxDate()

  return (
    <div className={cn("w-full flex flex-col gap-2", step === "navigation" && "xl:pt-20 2xl:pt-32")} id="chat-container">
      <AnimatePresence mode="wait">
        {step === "first" &&
          <ThemeSelection
            setStatus={setStatusHandler}
            status={status}
            setStep={stepHandler}
            onThemeSelect={themeSelectHandler}
            onReasoning={reasoningHandler}
            date={date!}
            setDate={setDate}
          />
        }

        {step === "date" &&
          <DateSelection
            maxEndDate={maxEndDate ? maxEndDate.max_end_date : format(addMonths(new Date(), 2), "yyyy-MM-dd")}
            date={date}
            setDate={setDate}
            setStep={stepHandler}
          />
        }

        {step === "result" && themeDetailData && (( themeDetailData.data.story_id === null && themeDetailData.data.status == 1 ) ? <ThemePostResult
          setStep={stepHandler}
          selectedTheme={selectedTheme}
          setMessage={messageHandler}
          themeDetailData={themeDetailData}
          setCanReturn={setCanReturn}
        /> : themeDetailData.data.status === 6
          ? <ThemeTemplateResult
            setStep={stepHandler}
            selectedTheme={selectedTheme}
            setMessage={messageHandler}
            themeDetailData={themeDetailData}
            setCanReturn={setCanReturn}
          />
          : <ThemeCampaignResult
            setStep={stepHandler}
            selectedTheme={selectedTheme}
            setMessage={messageHandler}
            themeDetailData={themeDetailData}
            setCanReturn={setCanReturn}
          />
        )
        }
        {step === "campaign" && (
          <ThemeCampaignSelection
            setStatus={setStatusHandler}
            status={status}
            setStep={stepHandler}
            onThemeSelect={themeSelectHandler}
            onReasoning={reasoningHandler}
            date={date!}
            setDate={setDate}
          />
        )}

        {step === "story" && selectedTheme && (
          <StorySelection
            imageId={imageId}
            setStep={stepHandler}
            onThemeSelect={storySelectHandler}
            onReasoning={reasoningHandler}
            event_id={selectedTheme} />
        )}

        {step === "reasoning" && (
          <Reasoning setStep={stepHandler} reasoning={reasoning} />
        )}

        {step === "success" && (
          <SuccessfulEvent message={message} setStep={stepHandler} canReturn={canReturn} setCanReturn={setCanReturn} />
        )}
        {step === "navigation" && (
          <ThemeNavigation setStep={stepHandler} setStatus={setStatus} onThemeSelect={themeSelectHandler} onReasoning={reasoningHandler} />
        )}
        {step === "create-post" && (
          <CreatePostTheme setStep={stepHandler} setMessage={messageHandler} setSelectedTheme={themeSelectFromCreatePostHandler} />
        )}
        {step === "calendar" && (
          <SocialCalendar setStep={stepHandler} />
        )}
      </AnimatePresence>

    </div>
  )
}

export default ChatWindow;
